
import { useEffect } from 'react';
import { useLocation, matchPath } from '@/lib/router-compat';
import { useAuth } from '@/context/AuthContext';
import { profileService } from '@/services/profileService';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

export const usePageViewTracker = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    const trackView = async () => {
      const articleMatch = matchPath('/article/:id', location.pathname);
      const identifier = articleMatch?.params.id;
      if (!identifier) return;

      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);
      let articleId = identifier;
      if (!isUuid) {
        const { data } = await supabase.from('articles').select('id').eq('slug', identifier).maybeSingle();
        if (!data?.id) return;
        articleId = data.id;
      }

      const sessionStorageKey = `read-article-${articleId}`;
      if (sessionStorage.getItem(sessionStorageKey)) return;

      const success = await profileService.addXP(5, 'article_read', { articleId }, 'articles', articleId);
      if (success) sessionStorage.setItem(sessionStorageKey, 'true');
      
      if (success) {
        queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
        queryClient.invalidateQueries({ queryKey: ['currentUserRank'] });
        queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
      }
    };

    // Use a timeout to avoid tracking on rapid navigation
    const timeoutId = setTimeout(trackView, 500);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, isAuthenticated, user, queryClient]);
};
